import React, { useEffect, useRef, useState } from 'react';
import { Loader2, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import DialogShell from '../ui/DialogShell';

export type FeedbackSentiment = 'positive' | 'negative' | null;
const FEEDBACK_COMMENT_MAX_CHARS = 5000;

interface FeedbackModalProps {
    isOpen: boolean;
    initialSentiment?: FeedbackSentiment;
    onClose: () => void;
    onSubmit: (payload: { sentiment: FeedbackSentiment; comment: string }) => Promise<void>;
}

export default function FeedbackModal({
    isOpen,
    initialSentiment = null,
    onClose,
    onSubmit,
}: FeedbackModalProps) {
    const titleId = React.useId();
    const initialFocusRef = useRef<HTMLTextAreaElement>(null);
    const [sentiment, setSentiment] = useState<FeedbackSentiment>(initialSentiment);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setSentiment(initialSentiment);
        setComment('');
        setError(null);
        setIsSubmitting(false);
    }, [isOpen, initialSentiment]);

    if (!isOpen) return null;

    const canSubmit = Boolean(sentiment || comment.trim()) && !isSubmitting;
    const displayError = error && error.length > 200 ? `${error.slice(0, 200)}...` : error;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) return;
        setIsSubmitting(true);
        setError(null);
        try {
            await onSubmit({ sentiment, comment: comment.trim() });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Feedback could not be sent';
            setError(message || 'Feedback could not be sent');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <DialogShell
            open={isOpen}
            onClose={onClose}
            labelledBy={titleId}
            backdropPolicy={isSubmitting || sentiment !== null || comment !== '' ? 'static' : 'dismiss'}
            initialFocusRef={initialFocusRef}
            overlayClassName="flex items-center justify-center px-4"
            panelClassName="w-full max-w-md border border-border-default overflow-hidden"
        >
            <form onSubmit={handleSubmit}>
                <div className="flex items-center justify-between border-b border-divider px-4 py-3">
                    <h2 id={titleId} className="text-sm font-semibold text-fg">Feedback</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="rounded p-1 text-fg-tertiary transition-colors hover:bg-bg-warm hover:text-fg"
                        aria-label="Close feedback"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-4 px-4 py-4">
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() => setSentiment(sentiment === 'positive' ? null : 'positive')}
                            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-3 text-sm font-medium transition-colors ${
                                sentiment === 'positive'
                                    ? 'border-success bg-success/10 text-success'
                                    : 'border-border-default text-fg-secondary hover:bg-bg-warm'
                            }`}
                            aria-pressed={sentiment === 'positive'}
                        >
                            <ThumbsUp className="h-5 w-5" />
                            Helpful
                        </button>
                        <button
                            type="button"
                            onClick={() => setSentiment(sentiment === 'negative' ? null : 'negative')}
                            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-3 text-sm font-medium transition-colors ${
                                sentiment === 'negative'
                                    ? 'border-danger bg-danger/10 text-danger'
                                    : 'border-border-default text-fg-secondary hover:bg-bg-warm'
                            }`}
                            aria-pressed={sentiment === 'negative'}
                        >
                            <ThumbsDown className="h-5 w-5" />
                            Not helpful
                        </button>
                    </div>

                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-fg-secondary">Comment</span>
                        <textarea
                            ref={initialFocusRef}
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                            rows={4}
                            maxLength={FEEDBACK_COMMENT_MAX_CHARS}
                            className="w-full resize-none rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg outline-none transition-colors placeholder:text-fg-tertiary focus:border-accent focus:ring-1 focus:ring-accent/20"
                            placeholder="Write what BARK should keep, fix, or explain differently..."
                        />
                    </label>

                    {displayError && (
                        <div className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                            {displayError}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-divider px-4 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="rounded-md px-3 py-2 text-sm font-medium text-fg-secondary transition-colors hover:bg-bg-warm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!canSubmit}
                        className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-bg-warm disabled:text-fg-tertiary"
                    >
                        {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        Send
                    </button>
                </div>
            </form>
        </DialogShell>
    );
}
